<template>
  <div class="flex items-center gap-x-4">
    <TPillBar
      ref="sideScrollContainerRef"
      class="px-3 lg:px-4 py-3 lg:py-4 scrollbar-hidden"
    >
      <TPill
        ref="helpMeSearchPillRef"
       
        is-highlighted
        @click="handleHelpMeSearchClick"
      >
        <template #icon>
          <TIcon
            :icon="['fal', 'hand-wave']"
            size="lg"
            :class="{ 'animate-wiggle': canAnimateSearchHelpWidgetIcon }"
          />
        </template>
        {{ t('helpMeSearch.trigger') }}
      </TPill>

      <SearchHelpWidgetModal v-model:visible="showHelpMeSearch" />

      <TPill
       
        has-trigger
        :is-active="hasDrivableTypes"
        @click="handleDrivableFilterClick"
      >
        <template #icon>
          <TIcon :icon="['fal', 'rv']" />
        </template>
        {{ t('searchFilters.rvType.drivable.title') }}
      </TPill>

      <TPill
       
        has-trigger
        :is-active="hasTowableTypes"
        @click="handleTowableFilterClick"
      >
        <template #icon>
          <TIcon :icon="['fal', 'caravan']" />
        </template>
        {{ t('searchFilters.rvType.towable.title') }}
      </TPill>

      <SearchFiltersQuickFilters />

      <TPill
       
        has-trigger
        :is-active="hasActiveFilters"
        @click="handleMoreFiltersClick"
      >
        <template #icon>
          <TIcon :icon="['fal', 'sliders-simple']" />
        </template>
        {{ t('searchFilters.moreFilters.title') }}
      </TPill>
    </TPillBar>

    <TModalCritical
      v-model:visible="showDrivableFilter"
      :title="t('searchFilters.rvType.drivable.title')"
      size="3xl"
      enable-scroll
      :label-primary="t('actions.apply')"
      :label-tertiary="t('actions.reset')"
      @action:primary="handleDrivableFilterApply"
      @action:tertiary="handleDrivableFilterReset"
    >
      <LazySearchFilterRvType
        v-model="drivableSelection"
        type="drivable"
      />
    </TModalCritical>

    <TModalCritical
      v-model:visible="showTowableFilter"
      :title="t('searchFilters.rvType.towable.title')"
      size="3xl"
      enable-scroll
      :label-primary="t('actions.apply')"
      :label-tertiary="t('actions.reset')"
      @action:primary="handleTowableFilterApply"
      @action:tertiary="handleTowableFilterReset"
    >
      <LazySearchFilterRvType
        v-model="towableSelection"
        type="towable"
      />
    </TModalCritical>

    <TModalCritical
      v-model:visible="showMoreFilters"
      :title="t('searchFilters.title')"
      size="3xl"
      enable-scroll
     
    >
      <template #default="{ dismiss }">
        <LazySearchFiltersMoreFilters @close="dismiss()" />
      </template>
    </TModalCritical>

    <TPopover
      ref="helpMeSearchPopoverEl"
      placement="bottom"
      class="shadow-lg max-w-[320px]"
      is-inverted
      @hide="dismissCookie()"
    >
      <template #default>
        <THeading
          :level="5"
          tag="h3"
          class="mb-2"
        >
          {{ t('helpMeSearch.popover.title') }}
        </THeading>

        <p>
          {{ t('helpMeSearch.popover.content') }}
        </p>

        <div class="mt-4 flex">
          <TButton
            class="ml-auto"
            variant="secondary"
            @click="handleHelpMeSearchPopoverGotIt"
          >
            {{ t('actions.tryNow') }}
          </TButton>
        </div>
      </template>
    </TPopover>
  </div>
</template>

<script setup lang="ts">
import type { TPopover } from '#components'
import type { ComponentInstance } from 'vue'
import { COOKIES } from '#shared/constants/cookies'
import { ONE_HUNDRED_EIGHTY_DAYS_IN_SECONDS } from '#shared/constants/durations'
import { type RV_TYPE_CATEGORY, type RV_TYPES, MOTORIZED_RV_TYPES, TOWABLE_RV_TYPES } from '~/constants/rv'

const { t } = useI18n()
const { applyMenuFilter, searchData, navigateToRvSearch } = useSearch()
const { isMobile } = useDevice()

const sideScrollContainerRef = useTemplateRef('sideScrollContainerRef')
useSideScroll(sideScrollContainerRef)

const drivableSelection = ref<RV_TYPES[]>([])
const towableSelection = ref<RV_TYPES[]>([])

const hasActiveFilters = computed(() => {
  // Mobile-only filters
  if (searchData.value.delivery && isMobile) {
    return true
  }
  if (searchData.value.instantBook && isMobile) {
    return true
  }

  // Filters
  if (searchData.value.minPrice || searchData.value.maxPrice) {
    return true
  }
  if (searchData.value.amenities?.length) {
    return true
  }
  if (searchData.value.minWeight || searchData.value.maxWeight) {
    return true
  }
  if (searchData.value.minLength || searchData.value.maxLength) {
    return true
  }
  if (searchData.value.minYear || searchData.value.maxYear) {
    return true
  }

  if (searchData.value.rvBrand) {
    return true
  }
  if (searchData.value.airportPickup) {
    return true
  }

  if (searchData.value.festivalFriendly) {
    return true
  }
  if (searchData.value.unlimitedMileage) {
    return true
  }
  if (searchData.value.guests?.petFriendly) {
    return true
  }
  if (searchData.value.experienceNotRequired) {
    return true
  }
  if (searchData.value.isSuperhost) {
    return true
  }

  return false
})

const hasDrivableTypes = computed(() => MOTORIZED_RV_TYPES.some((type) => searchData.value.types?.includes(type)))
const hasTowableTypes = computed(() => TOWABLE_RV_TYPES.some((type) => searchData.value.types?.includes(type)))

async function handleUpdateRvTypesFilter(
  category: RV_TYPE_CATEGORY,
  rvTypes: RV_TYPES[],
) {
  const categoryTypes = category === 'drivable' ? MOTORIZED_RV_TYPES : TOWABLE_RV_TYPES
  const otherCategoryTypes = category === 'drivable' ? TOWABLE_RV_TYPES : MOTORIZED_RV_TYPES

  const selectedFromCategory = categoryTypes.filter((type) => rvTypes.includes(type))
  const selectedFromOther = otherCategoryTypes.filter((type) =>
    searchData.value.types.includes(type),
  )

  applyMenuFilter()

  await navigateToRvSearch({
    Types: [...selectedFromCategory, ...selectedFromOther],
  })
}

function trackMenuOpened(menuName: Parameters<typeof trackSearchFilterMenuOpened>[0]['menuName']) {
  if (!menuName) return

  trackSearchFilterMenuOpened({
    menuName,
  })
}

const showDrivableFilter = ref(false)
const showTowableFilter = ref(false)
const showMoreFilters = ref(false)

function handleDrivableFilterClick() {
  trackMenuOpened('Drivable')

  if (!showDrivableFilter.value) {
    drivableSelection.value = MOTORIZED_RV_TYPES.filter((type) => searchData.value.types?.includes(type))
  }

  showDrivableFilter.value = !showDrivableFilter.value
}

function handleTowableFilterClick() {
  trackMenuOpened('Towable')

  if (!showTowableFilter.value) {
    towableSelection.value = TOWABLE_RV_TYPES.filter((type) => searchData.value.types?.includes(type))
  }

  showTowableFilter.value = !showTowableFilter.value
}

function handleMoreFiltersClick() {
  trackMenuOpened('Filters')
  showMoreFilters.value = !showMoreFilters.value
}

function handleDrivableFilterApply(dismiss: () => void) {
  handleUpdateRvTypesFilter('drivable', drivableSelection.value)
  dismiss()
}

function handleDrivableFilterReset(dismiss: () => void) {
  drivableSelection.value = []
  handleUpdateRvTypesFilter('drivable', [])
  dismiss()
}

function handleTowableFilterApply(dismiss: () => void) {
  handleUpdateRvTypesFilter('towable', towableSelection.value)
  dismiss()
}

function handleTowableFilterReset(dismiss: () => void) {
  towableSelection.value = []
  handleUpdateRvTypesFilter('towable', [])
  dismiss()
}

const showHelpMeSearch = ref(false)

function handleHelpMeSearchClick() {
  helpMeSearchPopoverEl.value?.hide()
  showHelpMeSearch.value = !showHelpMeSearch.value
}

const canAnimateSearchHelpWidgetIcon = computed(() => {
  if (hasDrivableTypes.value || hasTowableTypes.value) {
    return false
  }

  if (searchData.value.delivery) {
    return false
  }

  if (searchData.value?.guests?.petFriendly) {
    return false
  }

  if (searchData.value?.festivalFriendly) {
    return false
  }

  if (searchData.value?.maxPrice || searchData.value?.minPrice) {
    return false
  }

  return true
})

const helpMeSearchPillRef = useTemplateRef('helpMeSearchPillRef')
const helpMeSearchPopoverEl = ref<ComponentInstance<typeof TPopover>>()

const { isDismissed, dismissCookie } = useDismissCookie({
  id: COOKIES.searchHelpPopoverDismissed,
  expiresInSeconds: ONE_HUNDRED_EIGHTY_DAYS_IN_SECONDS,
})

const route = useRoute()
const { isFeatureEnabled } = useExperiment()
onMounted(() => {
  if (route.query?.help !== undefined) {
    showHelpMeSearch.value = true
  }
  else if (!isDismissed.value && isFeatureEnabled('rvz26988SearchHelpWidgetShowPopover')) {
    helpMeSearchPopoverEl.value?.toggleShow(helpMeSearchPillRef.value?.$el)
  }
})

function handleHelpMeSearchPopoverGotIt() {
  helpMeSearchPopoverEl.value?.hide()
  handleHelpMeSearchClick()
}
</script>

<i18n src="~~/i18n/locales/common/search/filters/index.json" lang="json" />

<i18n src="~~/i18n/locales/common/search/filters/rv-types.json" lang="json" />

<i18n src="~~/i18n/locales/common/search/help-widget.json" lang="json" />
